import { Filter, TableRow } from './table-models';

export type FiscalPrintOperation =
    | 'PRINT_SALE_RECEIPT'
    | 'PRINT_SALE_INVOICE'
    | 'PRINT_STORNO_RECEIPT'
    | 'PRINT_STORNO_INVOICE'
    | 'PRINT_DUPLICATE_RECEIPT'
    | 'PRINT_DUPLICATE_INVOICE'
    | 'PRINT_CARD_SLIP'
    | 'PRINT_REFUND_SLIP'
    | 'PRINT_X_REPORT'
    | 'PRINT_Z_REPORT';

export type FiscalPrintOutcome =
    | 'PENDING'
    | 'SUCCESS'
    | 'SUCCESS_WITH_WARNING'
    | 'PRINT_SUCCEEDED_NUMBER_UNREADABLE'
    | 'PARTIAL_OPERATOR_CONFIRMED_OK'
    | 'PARTIAL_OPERATOR_FLAGGED_INCOMPLETE'
    | 'FAILED_BEFORE_TOTAL'
    | 'FAILED_AFTER_TOTAL_NUMBER_UNREADABLE'
    | 'FAILED'
    | 'ABORTED';

export type OperatorConfirmation = 'RECEIPT_OK' | 'RECEIPT_INCOMPLETE' | 'RECEIPT_WRONG';

export const ALL_PRINT_OUTCOMES: FiscalPrintOutcome[] = [
    'PENDING',
    'SUCCESS',
    'SUCCESS_WITH_WARNING',
    'PRINT_SUCCEEDED_NUMBER_UNREADABLE',
    'PARTIAL_OPERATOR_CONFIRMED_OK',
    'PARTIAL_OPERATOR_FLAGGED_INCOMPLETE',
    'FAILED_BEFORE_TOTAL',
    'FAILED_AFTER_TOTAL_NUMBER_UNREADABLE',
    'FAILED',
    'ABORTED',
];

export const ALL_PRINT_OPERATIONS: FiscalPrintOperation[] = [
    'PRINT_SALE_RECEIPT',
    'PRINT_SALE_INVOICE',
    'PRINT_STORNO_RECEIPT',
    'PRINT_STORNO_INVOICE',
    'PRINT_DUPLICATE_RECEIPT',
    'PRINT_DUPLICATE_INVOICE',
    'PRINT_CARD_SLIP',
    'PRINT_REFUND_SLIP',
    'PRINT_X_REPORT',
    'PRINT_Z_REPORT',
];

/**
 * Server-side audit row for a single fiscal-print operation.
 * Mirrors `FiscalPrintAttemptVO` in client-service.
 */
export interface FiscalPrintAttempt extends TableRow {
    id: number;
    clientUuid: string;

    createdAt: string;
    updatedAt?: string;
    startedAt?: string;
    sdkResolvedAt?: string;
    operatorConfirmedAt?: string;

    operation: FiscalPrintOperation;
    outcome: FiscalPrintOutcome;
    paymentMethod?: string;

    documentId?: number;
    invoicingId?: number;
    saleId?: number;
    userId?: number;
    branchId?: number;

    physicalReceiptNumber?: string;
    fiscalCounter?: string;
    amountCents?: number;
    currency?: string;

    relatedCardTransactionKey?: string;
    relatedRefundTransactionKey?: string;

    sdkWarning?: string;
    errorMessage?: string;
    errorCode?: string;

    operatorConfirmation?: OperatorConfirmation;
    operatorNote?: string;

    retryOfClientUuid?: string;

    printerModel?: string;
    printerSerial?: string;

    clientAppVersion?: string;
    deviceModel?: string;
    deviceManufacturer?: string;
    osVersion?: string;
    deviceId?: string;
}

export interface PrinterAttemptQueryParams {
    page?: number;
    itemsPerPage?: number;
    outcome?: FiscalPrintOutcome;
    operation?: FiscalPrintOperation;
    filterUserId?: number;
    deviceId?: string;
    search?: string;
    fromDate?: string;
    toDate?: string;
}

/**
 * `Filter.tags` carries the multi-selected outcomes — backend honours one at a
 * time today. Matches the CardAttempts pattern.
 */
export const toPrinterAttemptParams = (
    filter: Filter | undefined,
    page: number,
    itemsPerPage: number,
): PrinterAttemptQueryParams => {
    const out: PrinterAttemptQueryParams = { page, itemsPerPage };
    if (!filter) return out;
    if (filter.tags && filter.tags.length > 0) {
        out.outcome = filter.tags[0] as FiscalPrintOutcome;
    }
    if (filter.search && filter.search.trim().length > 0) {
        out.search = filter.search.trim();
    }
    // Pad to ISO_DATE_TIME so Spring's @DateTimeFormat(ISO.DATE_TIME) parses.
    // Date picker emits ISO_DATE ("2026-05-12"); we pad start/end of day.
    if (filter.after) out.fromDate = normalizeDate(filter.after, '00:00:00');
    if (filter.before) out.toDate = normalizeDate(filter.before, '23:59:59');
    if (filter.id) {
        const parsed = Number(filter.id);
        if (Number.isFinite(parsed)) out.filterUserId = parsed;
    }
    return out;
};

const normalizeDate = (value: string, padTime: string): string => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value}T${padTime}`;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return `${value}:00`;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) return value;
    const d = new Date(value);
    if (!isNaN(d.getTime())) {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}T${padTime}`;
    }
    return value;
};
